import {
	CheckOutlined,
	DollarCircleOutlined,
	ExclamationCircleOutlined,
	FundOutlined,
	MoneyCollectOutlined,
	NumberOutlined,
	StopOutlined,
	ThunderboltOutlined,
	TrophyOutlined
} from '@ant-design/icons';
import { Col, Row, Select, Typography } from 'antd';
import HTMLReactParser from 'html-react-parser';
import millify from 'millify';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { LineChart, Loader } from '../components';
import {
	useGetCryptoDetailQuery,
	useGetCryptoHistoryQuery
} from '../services/cryptoApi';

const { Title, Text } = Typography;
const { Option } = Select;

const CryptoDetail = () => {
	const { coinId } = useParams();
	const [timePeriod, setTimePeriod] = useState('7d');
	const { data, isFetching } = useGetCryptoDetailQuery(coinId);
	const { data: coinHistory } = useGetCryptoHistoryQuery({
		coinId,
		timePeriod,
	});
	const cryptoDetail = data?.data?.coin;

	if (isFetching) return <Loader />;

	const time = ['3h', '24h', '7d', '30d', '1y', '3m', '3y', '5y'];

	const stats = [
		{
			title: 'Price to USD',
			value: `$ ${cryptoDetail?.price && millify(cryptoDetail?.price)}`,
			icon: <DollarCircleOutlined />,
		},
		{ title: 'Rank', value: cryptoDetail?.rank, icon: <NumberOutlined /> },
		{
			title: '24h Volume',
			value: `$ ${cryptoDetail?.volume && millify(cryptoDetail?.volume)}`,
			icon: <ThunderboltOutlined />,
		},
		{
			title: 'Market Cap',
			value: `$ ${
				cryptoDetail?.marketCap && millify(cryptoDetail?.marketCap)
			}`,
			icon: <DollarCircleOutlined />,
		},
		{
			title: 'All-time-high(daily avg.)',
			value: `$ ${
				cryptoDetail?.allTimeHigh?.price &&
				millify(cryptoDetail?.allTimeHigh?.price)
			}`,
			icon: <TrophyOutlined />,
		},
	];

	const genericStats = [
		{
			title: 'Number Of Markets',
			value: cryptoDetail?.numberOfMarkets,
			icon: <FundOutlined />,
		},
		{
			title: 'Number Of Exchanges',
			value: cryptoDetail?.numberOfExchanges,
			icon: <MoneyCollectOutlined />,
		},
		{
			title: 'Approved Supply',
			value: cryptoDetail?.supply?.confirmed ? (
				<CheckOutlined />
			) : (
				<StopOutlined />
			),
			icon: <ExclamationCircleOutlined />,
		},
		{
			title: 'Total Supply',
			value: `$ ${
				cryptoDetail?.supply?.total &&
				millify(cryptoDetail?.supply?.total)
			}`,
			icon: <ExclamationCircleOutlined />,
		},
		{
			title: 'Circulating Supply',
			value: `$ ${
				cryptoDetail?.supply?.circulating &&
				millify(cryptoDetail?.supply?.circulating)
			}`,
			icon: <ExclamationCircleOutlined />,
		},
	];

	return (
		<Col className='coin-detail-container'>
			<Col className='coin-heading-container'>
				<Title level={2} className='coin-name'>
					{cryptoDetail.name} ({cryptoDetail.symbol}) Price
				</Title>
				<p>
					{cryptoDetail.name} live price in US Dollar (USD). View
					value statistics, market cap and supply.
				</p>
			</Col>
			<Select
				defaultValue='7d'
				className='select-timeperiod'
				placeholder='Select Time Period'
				onChange={value => setTimePeriod(value)}
			>
				{time.map(date => (
					<Option key={date}>{date}</Option>
				))}
			</Select>
			<LineChart
				coinHistory={coinHistory}
				currentPrice={millify(cryptoDetail?.price)}
				coinName={cryptoDetail?.name}
			/>
			<Col className='stats-container'>
				<Col className='coin-value-statistics'>
					<Col className='oin-value-statistics-heading'>
						<Title level={3} className='coin-details-heading'>
							{cryptoDetail.name} Value Statistics
						</Title>
						<p>
							An overview showing the statistics of{' '}
							{cryptoDetail.name}, such as the base and quote
							currency, the rank, and trading volume.
						</p>
					</Col>
					{stats.map(({ icon, title, value }) => (
						<Col className='coin-stats' key={title}>
							<Col className='coin-stats-name'>
								<Text>{icon}</Text>
								<Text>{title}</Text>
							</Col>
							<Text className='stats'>{value}</Text>
						</Col>
					))}
				</Col>
				<Col className='other-stats-info'>
					<Col className='coin-value-statistics-heading'>
						<Title level={3} className='coin-details-heading'>
							Other Stats Info
						</Title>
						<p>
							An overview showing the statistics of{' '}
							{cryptoDetail.name}, such as the base and quote
							currency, the rank, and trading volume.
						</p>
					</Col>
					{genericStats.map(({ icon, title, value }) => (
						<Col className='coin-stats' key={title}>
							<Col className='coin-stats-name'>
								<Text>{icon}</Text>
								<Text>{title}</Text>
							</Col>
							<Text className='stats'>{value}</Text>
						</Col>
					))}
				</Col>
			</Col>
			<Col className='coin-desc-link'>
				<Row className='coin-desc'>
					<Title className='coin-details-heading' level={3}>
						What is {cryptoDetail.name}?
					</Title>
					{HTMLReactParser(cryptoDetail.description)}
				</Row>
				<Col className='coin-links'>
					<Title level={3} className='coin-details-heading'>
						{cryptoDetail.name} Links
					</Title>
					{cryptoDetail.links?.map(link => (
						<Row className='coin-link' key={link.name}>
							<Title level={5} className='link-name'>
								{link.type}
							</Title>
							<a href={link.url} target='_blank' rel='noreferrer'>
								{link.name}
							</a>
						</Row>
					))}
				</Col>
			</Col>
		</Col>
	);
};

export default CryptoDetail;
